import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { BlogService } from "../services/api/blog.service";
import BlogPreview, { PreviewSection } from "../shared/BlogPreview";

interface BlogDetailsViewProps {
  selectedBlog: string | null; // blog id
  onClose: () => void;
}

export function BlogDetailsView({
  selectedBlog,
  onClose,
}: BlogDetailsViewProps) {
  const [loading, setLoading] = useState(true);

  // Blog Data State matching BlogPreviewProps
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sections, setSections] = useState<PreviewSection[]>([]);
  const [doctorName, setDoctorName] = useState("");
  const [category, setCategory] = useState("");
  const [postedAt, setPostedAt] = useState("");
  const [mainImagePreview, setMainImagePreview] = useState("");

  // Fetch blog details
  const fetchBlogDetails = async (selectedBlog: string) => {
    try {
      setLoading(true);
      const response = await BlogService.getBlogsDetails(selectedBlog);

      if (response.success && response.data) {
        const data = response.data;
        setTitle(data.title || "");
        setDescription(data.description || "");
        setCategory(data.categories || "");
        setDoctorName(data.doctorName || "");
        setMainImagePreview(data.blogPoster || "");
        setPostedAt(data.postedAt || new Date().toISOString());

        // Map contents to sections
        if (data.contents && Array.isArray(data.contents)) {
          const mappedSections: PreviewSection[] = data.contents.map(
            (c: any) => ({
              id: c.id,
              title: c.title,
              content: c.description,
              images: c.images
                ? c.images.map((img: any) => ({
                    id: img.id,
                    url: img.image,
                  }))
                : [],
            }),
          );
          setSections(mappedSections);
        }
      } else {
        toast.error("Failed to fetch blog details");
        onClose();
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong while fetching blog details");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBlog) fetchBlogDetails(selectedBlog);
  }, [selectedBlog]);

  const calculateReadTime = () => {
    const stripHtml = (html: string) => html.replace(/<[^>]*>?/gm, "") || "";
    let text = stripHtml(description);
    sections.forEach((s) => {
      text += " " + stripHtml(s.content);
    });
    const words = text.trim().split(/\s+/).length;
    const time = Math.ceil(words / 200) || 1;
    return `${time} min read`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <BlogPreview
      onClose={onClose}
      title={title}
      description={description}
      mainImagePreview={mainImagePreview}
      sections={sections}
      doctorName={doctorName}
      category={category}
      date={new Date(postedAt).toLocaleDateString()}
      readTime={calculateReadTime()}
    />
  );
}
